import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Filter, Search } from 'lucide-react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api, getPublicImageUrl } from '../../utils/api';
import { getBookingAction } from '../../utils/auth';
import { toast } from 'sonner';
import Header from '../shared/Header';
import Footer from '../shared/Footer';
import SEO from '../shared/SEO';

const GalleryPage = () => {
  const [hairstyles, setHairstyles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    api.getPublicHairstyles().then(res => setHairstyles(res.data)).catch(console.error);
    api.getCategories().then(res => setCategories(res.data.filter(c => c.status === 'active'))).catch(console.error);
    
    // Pre-select category from URL param (from Services page "View Styles")
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [searchParams]);

  const filteredHairstyles = hairstyles.filter(style => {
    const matchesCategory = selectedCategory === 'all' || style.category === selectedCategory;
    const matchesSearch = style.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          style.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-[#FCFBF9]">
      <SEO
        title="Hairstyle Gallery"
        description="Browse our gallery of stunning hairstyles — knotless braids, cornrows, weaves, wig installations, and more. Find your perfect look."
        path="/gallery"
      />
      <Header />

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h1 className="font-serif text-4xl lg:text-5xl text-[#3E2723] mb-4 tracking-tight">Hairstyle Gallery</h1>
            <p className="text-[#786E66] text-lg">Explore our collection of beautiful hairstyles</p>
          </div>

          <div className="mb-8 flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#786E66]" />
              <input
                type="text"
                placeholder="Search hairstyles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none"
                data-testid="search-input"
              />
            </div>

            <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto">
              <Filter className="h-5 w-5 text-[#786E66] flex-shrink-0" />
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-full text-sm transition-all whitespace-nowrap ${
                  selectedCategory === 'all' 
                    ? 'bg-[#3E2723] text-white' 
                    : 'bg-white text-[#786E66] border border-[#E6DCD3] hover:border-[#3E2723]'
                }`}
                data-testid="filter-all"
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat.name}
                  onClick={() => setSelectedCategory(cat.name)}
                  className={`px-4 py-2 rounded-full text-sm transition-all whitespace-nowrap ${
                    selectedCategory === cat.name 
                      ? 'bg-[#3E2723] text-white' 
                      : 'bg-white text-[#786E66] border border-[#E6DCD3] hover:border-[#3E2723]'
                  }`}
                  data-testid={`filter-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredHairstyles.map((style, idx) => (
              <motion.div
                key={style.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
                className="group bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#E6DCD3] overflow-hidden"
                data-testid={`hairstyle-item-${idx}`}
              >
                <div className="overflow-hidden bg-[#F3EBE1] rounded-lg">
                  {style.cover_image || (style.images && style.images.length > 0) ? (
                    <img 
                      src={getPublicImageUrl(style.cover_image || style.images[0])} 
                      alt={style.name}
                      className="w-full h-72 object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-72 flex items-center justify-center">
                      <p className="text-sm text-[#786E66]">No image available</p>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  {style.featured && (
                    <span className="inline-block bg-[#C9A961] text-white text-xs px-3 py-1 rounded-full mb-3">Featured</span>
                  )}
                  <h3 className="font-serif text-xl text-[#3E2723] mb-2">{style.name}</h3>
                  <p className="text-[#786E66] text-sm mb-3 line-clamp-2">{style.description}</p>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-[#786E66] text-sm">{style.category}</p>
                    <p className="text-[#3E2723] font-medium text-lg">₦{style.price_ngn.toLocaleString()}</p>
                  </div>
                  <div className="flex items-center justify-between text-xs text-[#786E66] mb-4">
                    <span>{style.duration_minutes} mins</span>
                    {style.deposit_required && <span>Deposit: ₦{style.deposit_required.toLocaleString()}</span>}
                  </div>
                  <button
                    onClick={() => {
                      const result = getBookingAction(style.id);
                      if (result.action === 'navigate') {
                        navigate(result.path);
                      } else {
                        toast.info(result.message);
                      }
                    }}
                    className="w-full text-center bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-full px-6 py-2 transition-all hover:-translate-y-0.5"
                    data-testid={`book-button-${idx}`}
                  >
                    Book Now
                  </button>
                </div>
              </motion.div>
            ))}
          </div>

          {filteredHairstyles.length === 0 && (
            <div className="text-center py-12">
              <p className="text-[#786E66] text-lg">No hairstyles found matching your search.</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GalleryPage;
